import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import WaitlistDialog from "@/components/WaitlistDialog";
import { PreviewMock } from "@/components/RoadmapPreviews";
import { Sparkles, LineChart, Gift, Compass, ArrowRight, ThumbsUp, CalendarDays, Bell, CreditCard, Target, Link2, Layers, Newspaper, Download } from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";

const FEATURES = [
  {
    key: "income_target_ai",
    title: "AI Income Target Planner",
    icon: Target,
    tagline: "Tell us your monthly income goal — Claude reverse-solves how much to invest.",
    blurb:
      "Now live. Set a target like \"$1,500 / month in dividend income\" and pick any of your portfolios. The AI backruns the numbers — your portfolio's yield-on-cost, DRIP schedule, and historical dividend growth — and returns the exact upfront investment (both today's-yield and yield-growth scenarios) plus a monthly DCA contribution, with a plain-English explanation of the assumptions and risks.",
    eta: "Available now",
    available: true,
  },
  {
    key: "track_existing",
    title: "Track My Holdings",
    icon: LineChart,
    tagline: "Paste your real brokerage positions and DPP layers live income on top.",
    blurb:
      "Now live. Create multiple named accounts (Roth, Taxable, 401k), paste your positions as TICKER,SHARES,COST_BASIS, and DPP layers live prices + forward monthly income + yield-on-cost + upcoming ex-dividend dates on top. Per-position table shows % of portfolio and % of income contribution.",
    eta: "Available now",
    available: true,
  },
  {
    key: "broker_sync",
    title: "Broker Sync (one-click linking)",
    icon: Link2,
    tagline: "Auto-refresh your holdings via SnapTrade — no copy-paste.",
    blurb:
      "Now live in Production. Tap Connect brokerage on the Holdings page, choose your broker (Schwab, Fidelity, Vanguard, Robinhood, IBKR, and 40+ others), and DPP imports positions + cost basis + cash into a new account row. Read-only OAuth — DPP never places trades. Your SnapTrade user secret is encrypted at rest with Fernet.",
    eta: "Available now (Production)",
    available: true,
  },
  {
    key: "install_and_alerts",
    title: "Install the app + Ex-Div push alerts",
    icon: Download,
    tagline: "Install DPP to your home screen and get a phone push the day before every ex-dividend date.",
    blurb:
      "Now live. Tap Get the app in the footer (or Install on any browser prompt) to install DPP as a Progressive Web App on iOS, Android, or desktop — no App Store required. Once installed, enable push notifications on Holdings and we send a heads-up the day before each ex-dividend date across every ticker in every account.",
    eta: "Available now",
    available: true,
  },
  {
    key: "weekly_digest",
    title: "The DPP Weekly — personalized digest",
    icon: Newspaper,
    tagline: "Saturday morning email: your holdings snapshot, ex-div dates this week, and a bite-sized lesson.",
    blurb:
      "Now live. Every Saturday at 8am ET we email subscribers a compact digest — your tracked tickers, ex-dividend events landing in the next 7 days with projected payout amounts, plus one Investment 101 concept explained plainly. Tap the newsletter icon in the nav to subscribe. One-click unsubscribe in every email.",
    eta: "Available now",
    available: true,
  },
  {
    key: "cross_account_rollup",
    title: "Cross-Account Rollup + Portfolio Grouping",
    icon: Layers,
    tagline: "One view that merges Roth + Taxable + 401k into a single income picture — group them however you want.",
    blurb:
      "Now live. The Holdings page ships an \"All accounts\" rollup that combines every account into a single positions table with same-ticker aggregation, per-account drill-down, avg cost per share, total gain, forward monthly income, and a filter chip bar so you can scope to any portfolio you've created (e.g. Retirement · Taxable · Custom buckets — max 12 portfolios per user). Portfolio grouping is native — assign each account to a portfolio and every chart, table, and calendar filters by scope. One-click CSV export from every view.",
    eta: "Available now",
    available: true,
  },
  {
    key: "ai_balancing",
    title: "AI-Assisted Balancing & Optimization",
    icon: Sparkles,
    tagline: "Let Claude rebalance your holdings for a goal you choose.",
    blurb:
      "Now live. Pick a goal — Max yield-on-cost, Max CAGR, or Balanced — and Claude proposes new weights on your existing tickers, grounded in your backtest. See the before/after diff (YoC, CAGR, projected monthly income) before you accept.",
    eta: "Available now",
    available: true,
  },
  {
    key: "income_calendar",
    title: "Ex-Dividend Calendar + Monthly Income Chart",
    icon: CalendarDays,
    tagline: "A full-month calendar of every ex-div date + a 12–24 month income bar chart so you never miss a payday.",
    blurb:
      "Now live. The Holdings page ships a full-month ex-dividend calendar with past (from yfinance history, actual per-share amounts) + upcoming events, month navigation, per-day chips, and a month-total footer. Underneath it, a monthly dividend income bar chart (12M or 24M) shows the exact seasonality of your portfolio — the quarterly rhythm of SCHD/VYM/VTI vs the monthly steady flow of JEPI/O — with hover tooltips listing the top-5 contributing tickers per month.",
    eta: "Available now",
    available: true,
  },
  {
    key: "portfolio_value_growth",
    title: "Portfolio Value Growth Chart",
    icon: LineChart,
    tagline: "Daily snapshots plotted as a 30-day, 90-day, or 1-year growth curve.",
    blurb:
      "Now live. Every time DPP computes your Holdings summary it stores a daily snapshot of total value, cost basis, and forward income. The Portfolio value over time card renders those snapshots as a smooth area chart with 30D · 90D · 1Y range toggles, absolute $ change, and % gain across the visible window. Scoped by your active portfolio filter.",
    eta: "Available now",
    available: true,
  },
  {
    key: "competitor_scorecard",
    title: "DPP vs Competitor Scorecards",
    icon: Compass,
    tagline: "SEO-friendly head-to-head pages vs Snowball, Sharesight, and Seeking Alpha.",
    blurb:
      "Now live. Every dividend investor comparison-shops before subscribing — so we published a scorecard right on the Pricing page (DPP wins 19 of 21 categories at the lowest paid tier) plus three deep-dive landing pages at /vs/snowball, /vs/sharesight, and /vs/seeking-alpha with 15+ feature rows, 5 FAQ questions, and rich JSON-LD so Google and AI search surface DPP in \"DPP vs X\" queries.",
    eta: "Available now",
    available: true,
  },
  {
    key: "watchlist_alerts",
    title: "Watchlist Alerts",
    icon: Bell,
    tagline: "Get pinged when a starred ticker crosses a yield or price threshold.",
    blurb:
      "Set price and yield triggers on any ticker in your watchlist. When SCHD drops under $75 or JEPI's yield climbs past 8%, we email you and drop a notification on your Dashboard. Because the best entry is rarely the moment you happen to check.",
    eta: "Q2 2026",
  },
  {
    key: "referral_credits",
    title: "Referral Credits",
    icon: Gift,
    tagline: "Every friend you refer earns you a free month of Pro.",
    blurb:
      "The core plumbing is live — each user has a unique referral code and referral credits are granted server-side. The full public dashboard (share cards, leaderboard, monthly summary) ships next.",
    eta: "Available now (dashboard in progress)",
    available: true,
  },
  {
    key: "ticker_research",
    title: "Ticker Research",
    icon: Compass,
    tagline: "Deep-dive on any ticker before adding it to a portfolio.",
    blurb:
      "Now live. Company snapshot, closing-price chart with date-range selection, performance & risk stats, dividend safety score, payout ratio history, ex-dividend calendar, recent news, and an AI-written thesis. Search by ticker or browse by theme.",
    eta: "Available now",
    available: true,
  },
  {
    key: "team_stripe",
    title: "Team · Self-Serve Billing",
    icon: CreditCard,
    tagline: "Buy seats in-app with quantity-based Stripe billing.",
    blurb:
      "Right now, Team-tier onboarding runs through a quick sales call and the Admin Control Room's bulk-invite. Next: a proper quantity-based `pro_team_seat` Stripe subscription so admins can self-serve — pick seats, pay by card, and grow or shrink the team any month without emailing us.",
    eta: "Q2 2026",
  },
];

export default function ComingSoon() {
  const [waitlist, setWaitlist] = useState(null);
  usePageSeo({
    title: "Roadmap — What's shipping next on Dividend Portfolio Planner",
    description: "What's live and shipping next on DPP — AI Income Target Planner, live holdings tracking, and the public roadmap you can vote on.",
    path: "/coming-soon",
  });
  const [counts, setCounts] = useState({});
  const [youVotedFor, setYouVotedFor] = useState([]);
  const [pending, setPending] = useState({}); // per-feature loading

  const load = useCallback(async () => {
    try {
      const res = await api.get("/roadmap/votes");
      setCounts(res.data.counts || {});
      setYouVotedFor(res.data.you_voted_for || []);
    } catch (err) {
      console.debug("roadmap votes fetch failed (public endpoint, non-critical):", err?.message || err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleVote = async (feature_key) => {
    setPending((p) => ({ ...p, [feature_key]: true }));
    // optimistic
    const wasVoted = youVotedFor.includes(feature_key);
    setYouVotedFor((prev) => wasVoted ? prev.filter((k) => k !== feature_key) : [...prev, feature_key]);
    setCounts((prev) => ({ ...prev, [feature_key]: (prev[feature_key] || 0) + (wasVoted ? -1 : 1) }));
    try {
      const res = await api.post("/roadmap/vote", { feature_key });
      setCounts((prev) => ({ ...prev, [feature_key]: res.data.count }));
      if (res.data.action === "voted") toast.success("Vote counted — thanks!");
    } catch {
      // rollback
      setYouVotedFor((prev) => wasVoted ? [...prev, feature_key] : prev.filter((k) => k !== feature_key));
      setCounts((prev) => ({ ...prev, [feature_key]: (prev[feature_key] || 0) + (wasVoted ? 1 : -1) }));
      toast.error("Could not record vote");
    } finally {
      setPending((p) => ({ ...p, [feature_key]: false }));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <div className="text-center">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Roadmap</div>
        <h1 className="serif text-5xl sm:text-6xl mt-3 tracking-tight">
          What&apos;s coming next<span className="text-secondary">.</span>
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
          Here&apos;s what we&apos;re building. Vote 👍 on what you want most — we build the highest-voted feature next.
        </p>
      </div>

      <div className="mt-14 space-y-6">
        {FEATURES.map((f) => {
          const voted = youVotedFor.includes(f.key);
          const count = counts[f.key] || 0;
          return (
            <Card key={f.key} data-testid={`coming-soon-${f.key}`} className="card-hover">
              <CardContent className="p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-[1fr,340px] gap-6 items-start">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                        <f.icon className="w-5 h-5" />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="serif text-2xl">{f.title}</h2>
                        <Badge
                          variant={f.available ? "default" : "outline"}
                          className={`mono text-[10px] uppercase tracking-widest ${f.available ? "bg-primary text-primary-foreground" : "text-secondary border-secondary/40"}`}
                        >
                          {f.available ? "Live" : "Soon"}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-foreground/80 mt-3">{f.tagline}</p>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-xl">{f.blurb}</p>
                    <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mt-3">
                      ETA · {f.eta}
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid={`vote-${f.key}`}
                        onClick={() => toggleVote(f.key)}
                        disabled={pending[f.key]}
                        aria-pressed={voted}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                          voted
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card text-foreground border-border hover:border-primary/40"
                        } disabled:opacity-60`}
                      >
                        <ThumbsUp className={`w-4 h-4 ${voted ? "" : "text-muted-foreground"}`} />
                        <span className="mono text-xs">
                          {voted ? "Voted" : "Vote"} · <span data-testid={`vote-count-${f.key}`}>{count}</span>
                        </span>
                      </button>
                      {f.available ? (
                        <Link to="/pricing">
                          <Button variant="secondary" size="sm" className="rounded-full">
                            See how it works <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid={`coming-soon-notify-${f.key}`}
                          onClick={() => setWaitlist({
                            feature: f.title,
                            title: `${f.title} — soon`,
                            blurb: f.blurb,
                          })}
                        >
                          Notify me
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="md:order-2">
                    <PreviewMock variant={f.key} />
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2 text-center">
                      Preview · Not final
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-14 text-center text-sm text-muted-foreground space-y-3">
        <div>
          Watch feature walkthroughs on{" "}
          <a
            href="https://www.youtube.com/@DivPortplan"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
            data-testid="coming-soon-youtube"
          >
            YouTube · @DivPortplan
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <div>
          Have an idea? <a href="mailto:hello@dividendportfolioplanner.com" className="underline underline-offset-2 hover:text-primary">Email us</a> and we&apos;ll add it to the list.
        </div>
      </div>

      <WaitlistDialog
        open={!!waitlist}
        onOpenChange={(o) => !o && setWaitlist(null)}
        feature={waitlist?.feature || ""}
        title={waitlist?.title}
        blurb={waitlist?.blurb}
      />
    </div>
  );
}
